import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  Activity,
  Brain,
  ChevronLeft,
  ChevronRight,
  HeartHandshake,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

type GuideCard = {
  icon: LucideIcon;
  heading: string;
  body: string;
  link: { href: string; label: string };
  /** Pastel wash + accent ink come from the shared --qa-* accent tokens,
   *  so every card stays theme-aware in light and night mode. */
  wash: string;
  ink: string;
};

/**
 * Plain-English, crawlable introduction to trauma recovery, PTSD and
 * emotional healing on the home page. Educational only — no diagnosis
 * or treatment claims. Shown as a gentle carousel: one colourful card at a
 * time on phones, two side by side from tablet up.
 */
const cards: GuideCard[] = [
  {
    icon: HeartHandshake,
    heading: "Trauma Recovery",
    body: "Understand how trauma affects your body, emotions and everyday life.",
    link: { href: "/learn/trauma-nervous-system", label: "Explore trauma recovery" },
    wash: "--qa-gold-tint",
    ink: "--qa-gold-ink",
  },
  {
    icon: Brain,
    heading: "PTSD & C-PTSD",
    body: "Learn about flashbacks, triggers, nightmares and the effects of long-term trauma.",
    link: { href: "/learn/ptsd/understand/what-is-ptsd", label: "Understand PTSD & C-PTSD" },
    wash: "--qa-sky-tint",
    ink: "--qa-sky-ink",
  },
  {
    icon: Activity,
    heading: "Nervous System",
    body: "Explore fight, flight, freeze and fawn — and ways to help your body feel safer.",
    link: { href: "/learn/trauma-responses", label: "Explore your nervous system" },
    wash: "--qa-teal-tint",
    ink: "--qa-teal-ink",
  },
  {
    icon: Sparkles,
    heading: "Emotional Healing",
    body: "Gentle tools for grounding, breathing, journaling and rebuilding emotional safety.",
    link: { href: "/learn/grounding-techniques", label: "Start healing" },
    wash: "--qa-peach-tint",
    ink: "--qa-peach-ink",
  },
];

const AUTOPLAY_MS = 7000;

export function HomeRecoveryGuide() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    containScroll: "keepSnaps",
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi]);

  // Gentle auto-advance. It pauses while the pointer or keyboard focus is on
  // the carousel, and never runs when the visitor asks for reduced motion.
  useEffect(() => {
    if (!emblaApi || paused) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const id = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, paused]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollNext();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollPrev();
    }
  };

  const current = cards[Math.min(selected, cards.length - 1)];

  return (
    <section
      aria-labelledby="recovery-guide-heading"
      aria-roledescription="carousel"
      className="mx-auto w-full max-w-5xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <h2 id="recovery-guide-heading" className="mb-2 font-display text-xl text-foreground sm:text-2xl">
        Trauma recovery, PTSD &amp; emotional healing
      </h2>
      <p className="mb-4 max-w-3xl text-sm text-muted-foreground sm:text-base">
        Survivor-led education to help you make sense of your experiences and support your nervous
        system — at your own pace.
      </p>

      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-3 sm:gap-4">
            {cards.map((c, i) => (
              <article
                key={c.heading}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${cards.length}: ${c.heading}`}
                className="relative flex min-w-0 shrink-0 grow-0 basis-[86%] flex-col overflow-hidden rounded-3xl border border-border/60 bg-card/85 p-5 shadow-[0_10px_34px_-24px_rgba(90,70,30,0.55)] backdrop-blur-sm transition-colors duration-300 sm:basis-1/2 sm:p-6"
                style={{
                  borderColor: `color-mix(in oklab, var(${c.ink}) 45%, transparent)`,
                }}
              >
                {/* Pastel wash + oversized watermark icon */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: `linear-gradient(158deg, var(${c.wash}) 0%, var(${c.wash}) 26%, transparent 74%)`,
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute -end-5 -top-5 opacity-[0.13]"
                  style={{ color: `var(${c.ink})` }}
                >
                  <c.icon className="h-28 w-28" strokeWidth={1.2} />
                </div>

                <div className="relative flex h-full flex-col">
                  <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: `color-mix(in oklab, var(${c.ink}) 15%, transparent)`,
                      color: `var(${c.ink})`,
                    }}
                    aria-hidden="true"
                  >
                    <c.icon className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-3 font-display text-lg leading-snug text-foreground sm:text-xl">
                    {c.heading}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                  <a
                    href={c.link.href}
                    className="mt-auto inline-flex min-h-11 w-fit items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium no-underline transition-[background-color,transform] duration-200 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-deep)]"
                    style={{
                      borderColor: `color-mix(in oklab, var(${c.ink}) 38%, transparent)`,
                      color: `color-mix(in oklab, var(${c.ink}) 45%, var(--foreground))`,
                    }}
                  >
                    {c.link.label}
                    <span aria-hidden="true">
                      &nbsp;&rarr;
                    </span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>

        <p className="sr-only" aria-live="polite">
          Slide {selected + 1} of {cards.length}: {current?.heading}
        </p>

        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            aria-label="Previous topic"
            onClick={scrollPrev}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-card text-[var(--gold-deep)] transition hover:border-gold hover:shadow-[var(--shadow-gold)]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2">
            {snaps.map((_, i) => {
              const c = cards[Math.min(i, cards.length - 1)];
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to topic ${i + 1}: ${c.heading}`}
                  aria-current={i === selected}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === selected ? "w-6" : "w-2 opacity-45"
                  }`}
                  style={{
                    backgroundColor:
                      i === selected ? `var(${c.ink})` : `color-mix(in oklab, var(${c.ink}) 55%, transparent)`,
                  }}
                />
              );
            })}
          </div>

          <button
            type="button"
            aria-label="Next topic"
            onClick={scrollNext}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-card text-[var(--gold-deep)] transition hover:border-gold hover:shadow-[var(--shadow-gold)]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Educational content only — not a substitute for professional care. If you are in danger or
        crisis, please use our{" "}
        <a href="/crisis" className="underline underline-offset-2">
          crisis support
        </a>{" "}
        page or contact local emergency services.
      </p>
    </section>
  );
}
